import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { MATURE_RENDER_VIEW_PRESET, createDefaultAppearance, getMaterialDefinition, renderHdrDefinitions, type RenderEditorConfig, type RenderGroupAppearance, type RenderGroupKind, type RenderMeshGroup, type RenderSceneDocument, type RenderViewState } from "./scene-contract";
import { createAdvancedGemstoneMaterial } from "./gemstone-material";

type MeshEntry = { mesh: THREE.Mesh; meshName: string; autoKind: RenderGroupKind };

type FbxRenderCoreOptions = {
  fitPadding?: number;
  fitAzimuth?: number;
  onMeshSelect?: (meshName: string) => void;
};

export type FbxRenderCore = {
  load: (url: string, fileNameOrType?: string) => Promise<RenderMeshGroup[]>;
  update: (scene: RenderSceneDocument, config: RenderEditorConfig) => void;
  setHelpers: (helpers: { grid: boolean; axes: boolean }) => void;
  resetView: () => void;
  fitView: () => void;
  capturePreview: () => string | null;
  dispose: () => void;
};

type ModelFrame = {
  size: number;
  radius: number;
};

function inferKind(name: string): RenderGroupKind {
  if (/^(g\d*[_-]|gem\d*[_-])/i.test(name) || /(gem|stone|diamond|jewel|crystal|ruby|sapphire|emerald)/i.test(name)) return "gem";
  if (/^metal\d*[_-]/i.test(name) || /(metal|gold|silver|platinum|ring|band|mount|setting|frame)/i.test(name)) return "metal";
  return "unassigned";
}

function meshGroupId(meshName: string) {
  return `mesh:${meshName}`;
}

async function loadModelObject(url: string, fileNameOrType?: string): Promise<THREE.Object3D> {
  const typeHint = fileNameOrType?.trim().toLowerCase() ?? "";
  const isStl = typeHint === "stl" || typeHint.endsWith(".stl") || /\.stl(?:$|[?#])/i.test(url);
  if (!isStl) {
    return new FBXLoader().loadAsync(url);
  }

  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(`Model request failed with status ${response.status}.`);
  }

  const body = await response.arrayBuffer();
  const geometry = new STLLoader().parse(body);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshPhysicalMaterial({ color: "#d9c17c", metalness: 0.85, roughness: 0.28 }),
  );
  mesh.name = "T_5G_Gold_01_N";
  const group = new THREE.Group();
  group.name = "legacy-stl-model";
  group.add(mesh);
  return group;
}

function isLegacyAggregateGroupId(id?: string) {
  return id === "metal" || id === "gemstone" || id === "unassigned";
}

function defaultLabel(kind: RenderGroupKind) {
  if (kind === "gem") return "Gemstone";
  if (kind === "metal") return "Metal";
  return "Unclassified";
}

function makeAutoGroups(entries: MeshEntry[]) {
  return entries.map((entry) => ({
    id: meshGroupId(entry.meshName),
    label: entry.meshName || defaultLabel(entry.autoKind),
    kind: entry.autoKind,
    meshNames: [entry.meshName],
  }));
}

function groupsFromAssignments(entries: MeshEntry[], document: RenderSceneDocument) {
  const hasLegacyAggregates = document.meshGroups.some((group) => isLegacyAggregateGroupId(group.id) && group.meshNames.length > 1);
  const assignments = new Map(document.meshAssignments.map((assignment) => [assignment.meshName, assignment]));
  if (!document.meshGroups.length || hasLegacyAggregates) {
    return entries.map((entry) => {
      const assignment = assignments.get(entry.meshName);
      const kind = assignment?.kind ?? entry.autoKind;
      return {
        id: assignment?.groupId && !isLegacyAggregateGroupId(assignment.groupId) ? assignment.groupId : meshGroupId(entry.meshName),
        label: entry.meshName || defaultLabel(kind),
        kind,
        meshNames: [entry.meshName],
      };
    });
  }

  const fromDocument = document.meshGroups;
  const definitions = new Map(fromDocument.map((group) => [group.id, { ...group, meshNames: [] as string[] }]));
  entries.forEach((entry) => {
    const assignment = assignments.get(entry.meshName);
    const id = assignment?.groupId ?? meshGroupId(entry.meshName);
    const kind = assignment?.kind ?? definitions.get(id)?.kind ?? entry.autoKind;
    if (!definitions.has(id)) definitions.set(id, { id, label: entry.meshName || defaultLabel(kind), kind, meshNames: [] });
    definitions.get(id)!.meshNames.push(entry.meshName);
  });
  return [...definitions.values()].filter((group) => group.meshNames.length);
}

export function createFbxRenderCore(container: HTMLElement, options: FbxRenderCoreOptions = {}): FbxRenderCore {
  const fitPadding = options.fitPadding ?? 1.45;
  const fitAzimuth = options.fitAzimuth ?? 0;
  const scene = new THREE.Scene();
  // Product configuration should preserve proportions while orbiting; use the
  // same orthographic presentation mode as the mature rendering workspace.
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, MATURE_RENDER_VIEW_PRESET.cameraNear, MATURE_RENDER_VIEW_PRESET.cameraFar);
  camera.position.fromArray(MATURE_RENDER_VIEW_PRESET.cameraPosition ?? [0, 4, 7]);
  camera.zoom = MATURE_RENDER_VIEW_PRESET.cameraZoom ?? 1.1;
  camera.up.set(0, 1, 0);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setClearColor("#000000", 1);
  container.replaceChildren(renderer.domElement);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = true;
  controls.minZoom = MATURE_RENDER_VIEW_PRESET.controlsMinZoom ?? 0.35;
  controls.maxZoom = 5;
  controls.minDistance = MATURE_RENDER_VIEW_PRESET.controlsMinDistance ?? 0.1;
  controls.maxDistance = MATURE_RENDER_VIEW_PRESET.controlsMaxDistance ?? 3.8;
  controls.autoRotate = MATURE_RENDER_VIEW_PRESET.autoRotate ?? false;
  controls.autoRotateSpeed = MATURE_RENDER_VIEW_PRESET.autoRotateSpeed ?? 2;
  controls.rotateSpeed = 0.72;
  controls.zoomSpeed = 0.82;
  controls.panSpeed = 0.65;
  controls.target.fromArray(MATURE_RENDER_VIEW_PRESET.target ?? [0, 0, 0]);
  const pmrem = new THREE.PMREMGenerator(renderer);
  const fallbackEnvironment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environment = fallbackEnvironment;
  const textureLoader = new THREE.TextureLoader();
  textureLoader.setCrossOrigin("anonymous");
  const hdrTextures = new Map<string, THREE.Texture>();
  const materialTextures = new Map<string, THREE.Texture>();
  let requestedHdrId = "";
  const hemi = new THREE.HemisphereLight("#ffffff", "#090909", 1.8);
  const key = new THREE.DirectionalLight("#ffffff", 3.2); key.position.set(2.8, 5.2, 5.6); key.castShadow = true;
  const rim = new THREE.DirectionalLight("#9fcaff", 1.1); rim.position.set(-4.5, 1.8, -3.2);
  const fill = new THREE.DirectionalLight("#fff0c8", 0.55); fill.position.set(-2.2, 2.4, 3.5);
  const grid = new THREE.GridHelper(2, 20, "#4b4f52", "#24282b");
  const axes = new THREE.AxesHelper(1);
  grid.visible = false;
  axes.visible = false;
  scene.add(hemi, key, rim, fill, grid, axes);

  let object: THREE.Object3D | null = null;
  let entries: MeshEntry[] = [];
  let modelFrame: ModelFrame | null = null;
  let animationFrame = 0;
  let pointerDown: { x: number; y: number } | null = null;
  const pointer = new THREE.Vector2();
  const raycaster = new THREE.Raycaster();
  const getView = (view?: RenderViewState) => ({ ...MATURE_RENDER_VIEW_PRESET, ...view });
  const setFrustum = (frameHeight: number) => {
    camera.userData.frameHeight = frameHeight;
    resize();
  };
  const applyCameraPreset = (view?: RenderViewState) => {
    const next = getView(view);
    camera.position.fromArray(next.cameraPosition ?? [0, 4, 7]);
    controls.target.fromArray(next.target ?? [0, 0, 0]);
    camera.zoom = next.cameraZoom ?? 1.1;
    camera.near = next.cameraNear ?? 0.1;
    camera.far = next.cameraFar ?? 1000;
    controls.minZoom = next.controlsMinZoom ?? 0.35;
    controls.maxZoom = typeof next.controlsMaxZoom === "number" ? next.controlsMaxZoom : Number.POSITIVE_INFINITY;
    controls.minDistance = next.controlsMinDistance ?? 0.1;
    controls.maxDistance = next.controlsMaxDistance ?? 3.8;
    controls.autoRotate = next.autoRotate ?? false;
    controls.autoRotateSpeed = next.autoRotateSpeed ?? 2;
    setFrustum(next.frustumSize ?? 6);
    controls.update();
  };
  const fitToModel = () => {
    if (!modelFrame) {
      applyCameraPreset();
      return;
    }
    const frameHeight = Math.max(modelFrame.size * fitPadding, MATURE_RENDER_VIEW_PRESET.frustumSize ?? 6);
    const distanceScale = Math.max(modelFrame.radius, 1);
    camera.position.set(
      Math.sin(fitAzimuth) * distanceScale * 7,
      distanceScale * 4,
      Math.cos(fitAzimuth) * distanceScale * 7,
    );
    controls.target.set(0, 0, 0);
    camera.zoom = MATURE_RENDER_VIEW_PRESET.cameraZoom ?? 1.1;
    camera.near = Math.max(0.001, distanceScale / 10000);
    camera.far = Math.max(1000, distanceScale * 64);
    controls.minDistance = Math.max(0.001, distanceScale * 0.01);
    controls.maxDistance = Math.max(distanceScale * 16, MATURE_RENDER_VIEW_PRESET.controlsMaxDistance ?? 3.8);
    setFrustum(frameHeight);
    controls.update();
  };
  const resize = () => {
    const width = Math.max(1, container.clientWidth);
    const height = Math.max(1, container.clientHeight);
    const aspect = width / height;
    const frameHeight = Number(camera.userData.frameHeight) || 2;
    camera.left = -(frameHeight * aspect) / 2;
    camera.right = (frameHeight * aspect) / 2;
    camera.top = frameHeight / 2;
    camera.bottom = -frameHeight / 2;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  };
  const resizeObserver = new ResizeObserver(resize); resizeObserver.observe(container); resize();
  const render = () => { controls.update(); renderer.render(scene, camera); animationFrame = window.requestAnimationFrame(render); }; render();

  const pickMesh = (clientX: number, clientY: number) => {
    if (!entries.length) return;
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.x = ((clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1;
    pointer.y = -(((clientY - rect.top) / Math.max(rect.height, 1)) * 2 - 1);
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(entries.map((entry) => entry.mesh), false);
    const hit = hits.find((item) => item.object instanceof THREE.Mesh);
    if (!hit) return;
    const entry = entries.find((item) => item.mesh === hit.object);
    if (!entry) return;
    options.onMeshSelect?.(entry.meshName);
  };

  const handlePointerDown = (event: PointerEvent) => {
    pointerDown = { x: event.clientX, y: event.clientY };
  };

  const handlePointerUp = (event: PointerEvent) => {
    if (!pointerDown) return;
    const moved = Math.hypot(event.clientX - pointerDown.x, event.clientY - pointerDown.y);
    pointerDown = null;
    if (moved <= 4) pickMesh(event.clientX, event.clientY);
  };

  renderer.domElement.addEventListener("pointerdown", handlePointerDown);
  renderer.domElement.addEventListener("pointerup", handlePointerUp);

  const setEnvironment = (hdr: (typeof renderHdrDefinitions)[number]) => {
    scene.background = new THREE.Color(hdr.background);
    requestedHdrId = hdr.id;
    const applyTexture = (texture: THREE.Texture) => {
      if (requestedHdrId !== hdr.id) return;
      scene.environment = texture;
    };
    const cached = hdrTextures.get(hdr.id);
    if (cached) {
      applyTexture(cached);
      return;
    }
    if (!hdr.assetPath) {
      scene.environment = fallbackEnvironment;
      return;
    }
    new RGBELoader().loadAsync(hdr.assetPath).then((texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      hdrTextures.set(hdr.id, texture);
      applyTexture(texture);
    }).catch(() => {
      if (requestedHdrId === hdr.id) scene.environment = fallbackEnvironment;
    });
  };

  const loadHdrEnvironment = (hdrId?: string) => {
    const hdr = renderHdrDefinitions.find((item) => item.id === hdrId);
    if (!hdr?.assetPath) return Promise.resolve<THREE.Texture | null>(null);
    const cached = hdrTextures.get(hdr.id);
    if (cached) return Promise.resolve(cached);
    return new RGBELoader().loadAsync(hdr.assetPath).then((texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      hdrTextures.set(hdr.id, texture);
      return texture;
    }).catch(() => null);
  };

  const applyPartEnvironment = (material: THREE.MeshPhysicalMaterial, hdrId?: string, exposure?: number) => {
    if (!hdrId) return;
    void loadHdrEnvironment(hdrId).then((texture) => {
      if (!texture) return;
      material.envMap = texture;
      material.envMapIntensity *= exposure ?? 1;
      material.needsUpdate = true;
    });
  };

  const loadMaterialTexture = (url?: string, colorSpace?: THREE.ColorSpace) => {
    if (!url) return Promise.resolve<THREE.Texture | null>(null);
    if (/\.svg(?:$|\?)/i.test(url)) return Promise.resolve<THREE.Texture | null>(null);
    const cached = materialTextures.get(url);
    if (cached) return Promise.resolve(cached);
    return textureLoader.loadAsync(url).then((texture) => {
      texture.colorSpace = colorSpace ?? THREE.NoColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      materialTextures.set(url, texture);
      return texture;
    }).catch(() => null);
  };

  const applyMetalMaps = (material: THREE.MeshPhysicalMaterial, appearance: RenderGroupAppearance) => {
    const definition = getMaterialDefinition(appearance.materialId);
    const baseColorMapUrl = appearance.baseColorMapUrl ?? (definition.baseColorMapUrl?.includes("/maps/") ? definition.baseColorMapUrl : undefined);
    void Promise.all([
      loadMaterialTexture(baseColorMapUrl, THREE.SRGBColorSpace),
      loadMaterialTexture(appearance.normalMapUrl ?? definition.normalMapUrl),
      loadMaterialTexture(appearance.roughnessMapUrl ?? definition.roughnessMapUrl),
      loadMaterialTexture(appearance.metalnessMapUrl ?? definition.metalnessMapUrl),
      loadMaterialTexture(appearance.aoMapUrl ?? definition.aoMapUrl),
    ]).then(([map, normalMap, roughnessMap, metalnessMap, aoMap]) => {
      if (map) material.map = map;
      if (normalMap) material.normalMap = normalMap;
      if (roughnessMap) material.roughnessMap = roughnessMap;
      if (metalnessMap) material.metalnessMap = metalnessMap;
      if (aoMap) material.aoMap = aoMap;
      material.needsUpdate = true;
    });
  };

  const apply = (document: RenderSceneDocument, config: RenderEditorConfig) => {
    const groups = groupsFromAssignments(entries, document);
    const groupByMesh = new Map(groups.flatMap((group) => group.meshNames.map((meshName) => [meshName, group] as const)));
    entries.forEach((entry) => {
      const group = groupByMesh.get(entry.meshName);
      const kind = group?.kind ?? entry.autoKind;
      const appearance = config.groups[group?.id ?? ""] ?? createDefaultAppearance(kind);
      const old = entry.mesh.material;
      const material = kind === "gem"
        ? createAdvancedGemstoneMaterial(appearance)
        : new THREE.MeshPhysicalMaterial({
            color: appearance.color, metalness: appearance.metalness, roughness: appearance.roughness,
            clearcoat: appearance.clearcoat, clearcoatRoughness: Math.min(0.3, appearance.roughness),
            envMapIntensity: appearance.envMapIntensity, transmission: 0,
            ior: 1.45, thickness: 0,
            attenuationColor: appearance.attenuationColor, attenuationDistance: appearance.attenuationDistance,
            transparent: false, side: THREE.DoubleSide,
          });
      entry.mesh.castShadow = true;
      entry.mesh.receiveShadow = true;
      entry.mesh.material = material;
      if (kind === "metal") applyMetalMaps(material, appearance);
      applyPartEnvironment(material, appearance.hdr, appearance.exposure);
      if (old && old !== material) { const materials = Array.isArray(old) ? old : [old]; materials.forEach((item) => item.dispose()); }
    });
    const hdr = renderHdrDefinitions.find((item) => item.id === config.hdr) ?? renderHdrDefinitions[0];
    setEnvironment(hdr); renderer.toneMappingExposure = config.exposure || hdr.exposure;
    scene.background = new THREE.Color(config.backgroundColor || hdr.background);
    hemi.intensity = hdr.environmentIntensity * 1.45; key.intensity = hdr.environmentIntensity * 2.4; rim.intensity = hdr.environmentIntensity * 0.95; fill.intensity = hdr.environmentIntensity * 0.42;
  };

  return {
    async load(url, fileNameOrType) {
      if (object) scene.remove(object);
      const loaded = await loadModelObject(url, fileNameOrType); object = loaded;
      const box = new THREE.Box3().setFromObject(object);
      const boxSize = box.getSize(new THREE.Vector3());
      const size = Math.max(boxSize.x, boxSize.y, boxSize.z, 0.1);
      const radius = Math.max(boxSize.length() / 2, 0.1);
      const center = box.getCenter(new THREE.Vector3());
      object.position.sub(center);
      modelFrame = { size, radius };
      fitToModel();
      scene.add(object);
      let sequence = 0;
      entries = [];
      object.traverse((node) => {
        if (!(node instanceof THREE.Mesh)) return;
        sequence += 1;
        const meshName = node.name || `mesh-${sequence}`;
        node.frustumCulled = false;
        if (!node.geometry.getAttribute("normal")) {
          node.geometry.computeVertexNormals();
        } else {
          node.geometry.normalizeNormals();
        }
        node.geometry.computeBoundingSphere();
        entries.push({ mesh: node, meshName, autoKind: inferKind(meshName) });
      });
      return makeAutoGroups(entries);
    },
    update: apply,
    setHelpers(helpers) {
      grid.visible = helpers.grid;
      axes.visible = helpers.axes;
    },
    resetView() {
      applyCameraPreset();
    },
    fitView() {
      fitToModel();
    },
    capturePreview() {
      try {
        controls.update();
        renderer.render(scene, camera);
        const source = renderer.domElement;
        const scale = Math.min(1, 960 / Math.max(source.width, source.height, 1));
        const preview = document.createElement("canvas");
        preview.width = Math.max(1, Math.round(source.width * scale));
        preview.height = Math.max(1, Math.round(source.height * scale));
        const context = preview.getContext("2d", { alpha: false });
        if (!context) return null;
        context.drawImage(source, 0, 0, preview.width, preview.height);
        return preview.toDataURL("image/jpeg", 0.84);
      } catch {
        return null;
      }
    },
    dispose() {
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      renderer.domElement.removeEventListener("pointerup", handlePointerUp);
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      controls.dispose();
      fallbackEnvironment.dispose();
      hdrTextures.forEach((texture) => texture.dispose());
      materialTextures.forEach((texture) => texture.dispose());
      pmrem.dispose();
      renderer.dispose();
      container.replaceChildren();
    },
  };
}
